import React, { useEffect, useRef, useState } from "react";
import { PawPrint, X, Check as CheckIcon } from "lucide-react";
import { toast } from "sonner";
import { track } from "../lib/analytics";
import { api } from "../lib/api";
import { absolutePlaceUrl } from "../lib/placeUrl";

/**
 * PostCheckinSharePrompt
 * ---------------------------------------------------------------------------
 * Non-blocking share nudge that appears after a successful check-in. Slides
 * up from the bottom of the viewport (200ms ease-out), auto-dismisses after
 * 8s, or can be manually shared/dismissed. Uses the same Web Share API
 * fallback strategy as `ShareButton.jsx` (clipboard copy when navigator.share
 * is missing or rejects), but with a different copy + GA4 events tuned to
 * the post-check-in moment.
 *
 * Lifecycle (controlled by parent):
 *   - Parent mounts <PostCheckinSharePrompt open={true} ... /> 1.5s AFTER
 *     the check-in success state — the drawer has already auto-closed by
 *     then so there's no overlap.
 *   - Prompt fires `post_checkin_share_shown` on open.
 *   - User can:
 *       a) Tap "Share" → native share or clipboard fallback, then close.
 *       b) Tap ✕ → fires `post_checkin_share_dismissed` with manual=true.
 *       c) Wait 8s → fires `post_checkin_share_dismissed` with auto=true.
 *     Any of those calls `onClose()` so parent can reset its state.
 *
 * Only ONE GA4 event fires per prompt instance — we lock with a ref so the
 * auto-dismiss timer never races against a manual share/dismiss.
 */

const SHARE_BASE = "https://pawground.app";

const supportsNativeShare = () => {
  if (typeof navigator === "undefined") return false;
  if (typeof navigator.share !== "function") return false;
  if (typeof navigator.canShare === "function") {
    try {
      return navigator.canShare({ url: SHARE_BASE });
    } catch {
      return false;
    }
  }
  return true;
};

const copyToClipboard = async (text) => {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // fall through to execCommand fallback
    }
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "absolute";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
};

// Builds the share text. Trims dog handling so 0/1/many all read naturally
// instead of producing dangling " with " or "& 0 more".
const buildShareText = (parkName, dogNames) => {
  const names = (dogNames || []).filter(Boolean);
  if (names.length === 0) return `I just checked in at ${parkName}! 🐾`;
  if (names.length === 1) return `I just checked in at ${parkName} with ${names[0]}! 🐾`;
  if (names.length === 2) return `I just checked in at ${parkName} with ${names[0]} & ${names[1]}! 🐾`;
  return `I just checked in at ${parkName} with ${names[0]} & ${names.length - 1} more! 🐾`;
};

const PostCheckinSharePrompt = ({ open, park, dogNames = [], userStats = null, checkinIds = [], onClose }) => {
  // Animation hook: `mounted=false` keeps card off-screen on first render,
  // then we flip to true on the next frame so the transform transition
  // actually runs (you can't transition from "no style" → "translateY(0)").
  const [mounted, setMounted] = useState(false);
  // Locks event firing so auto-dismiss + manual tap can't both fire GA
  // events for the same prompt instance.
  const settledRef = useRef(false);
  // ----- Dog Journal "note about your visit" state ------------------
  // `note` is the live textarea value (always controlled). `saveState`
  // walks through 'idle' → 'saving' → 'saved'; we never go back to
  // 'idle' from 'saved' in the same prompt instance — once a note is
  // saved the user must dismiss to write a new one. `dirtyRef` flips
  // true the moment the user focuses or types and is checked by the
  // 8-second auto-dismiss timer; if dirty, the timer cancels so the
  // user has unlimited time to finish writing.
  const [note, setNote] = useState("");
  const [saveState, setSaveState] = useState("idle"); // 'idle'|'saving'|'saved'
  const dirtyRef = useRef(false);
  const autoDismissTimerRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setMounted(false);
      settledRef.current = false;
      // Reset journal state when the prompt closes so a subsequent
      // check-in starts with a fresh textarea (not yesterday's draft).
      setNote("");
      setSaveState("idle");
      dirtyRef.current = false;
      if (autoDismissTimerRef.current) {
        clearTimeout(autoDismissTimerRef.current);
        autoDismissTimerRef.current = null;
      }
      return undefined;
    }
    // Fire impression event once when the card mounts.
    track("post_checkin_share_shown", { place_id: park?.id });
    const raf = requestAnimationFrame(() => setMounted(true));
    // Auto-dismiss after 8 seconds — but only if the user hasn't
    // started writing a note. The note textarea cancels this timer
    // on focus (see `handleNoteFocus`) so journal entries don't get
    // ripped out from under the user mid-thought.
    autoDismissTimerRef.current = setTimeout(() => {
      if (settledRef.current || dirtyRef.current) return;
      settledRef.current = true;
      track("post_checkin_share_dismissed", {
        place_id: park?.id,
        dismiss_type: "auto",
      });
      setMounted(false);
      // Wait for the slide-down animation to finish before unmounting.
      setTimeout(() => onClose?.(), 220);
    }, 8000);
    return () => {
      cancelAnimationFrame(raf);
      if (autoDismissTimerRef.current) {
        clearTimeout(autoDismissTimerRef.current);
        autoDismissTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, park?.id]);

  // ----- Journal note handlers -------------------------------------
  const handleNoteFocus = () => {
    // First touch of the textarea cancels the auto-dismiss so the
    // user can finish writing. We never re-arm it — they must
    // explicitly Share or X to leave.
    dirtyRef.current = true;
    if (autoDismissTimerRef.current) {
      clearTimeout(autoDismissTimerRef.current);
      autoDismissTimerRef.current = null;
    }
  };

  const handleNoteChange = (e) => {
    if (saveState === "saved") return; // locked after a successful save
    const v = e.target.value;
    // Hard 280-char clamp on the client too — the backend also enforces.
    setNote(v.length > 280 ? v.slice(0, 280) : v);
  };

  const handleSaveNote = async () => {
    const trimmed = note.trim();
    if (!trimmed || saveState !== "idle") return;
    if (!checkinIds || checkinIds.length === 0) {
      // No check-in id to attach to — shouldn't happen in practice
      // because the share prompt only opens after a successful POST
      // /checkins. Surface a quiet error and bail.
      toast.error("Couldn't save note — try again from your profile.");
      return;
    }
    setSaveState("saving");
    try {
      // Multi-dog check-ins create one row per (dog × park) — fan the
      // SAME note out to each row so visit history is consistent
      // regardless of which row the user opens later. Parallel calls
      // because the rows are independent and the user is staring at
      // a "Saved ✓" affordance.
      await Promise.all(
        checkinIds.map((id) =>
          api.patch(`/checkins/${id}/note`, { note: trimmed })
        )
      );
      track("checkin_note_saved", {
        place_id: park?.id,
        note_length: trimmed.length,
        checkin_count: checkinIds.length,
      });
      setSaveState("saved");
    } catch (e) {
      setSaveState("idle");
      toast.error(e?.response?.data?.detail || "Couldn't save note");
    }
  };

  const handleDismiss = () => {
    if (settledRef.current) return;
    settledRef.current = true;
    track("post_checkin_share_dismissed", {
      place_id: park?.id,
      dismiss_type: "manual",
    });
    setMounted(false);
    setTimeout(() => onClose?.(), 220);
  };

  const handleShare = async () => {
    if (settledRef.current || !park) return;
    // All detail URLs flow through the shared placeUrl() helper so
    // /park/{id}, /place/{slug}, and Fix #2 canonical
    // /{cat}/{city}/{slug} all resolve the same way.
    const url = absolutePlaceUrl(park, SHARE_BASE);
    const title = `${park.name} on Pawground`;
    const text = buildShareText(park.name, dogNames);
    let method = null;

    if (supportsNativeShare()) {
      try {
        await navigator.share({ title, text, url });
        method = "native";
      } catch (err) {
        // User cancelled the share sheet — leave the prompt visible so
        // they can try again or dismiss explicitly. No GA event fired.
        if (err?.name === "AbortError") return;
        // Other errors (e.g. NotAllowedError) → fall through to clipboard.
      }
    }

    if (!method) {
      const ok = await copyToClipboard(url);
      method = "clipboard";
      if (ok) {
        toast("Link copied!", {
          duration: 2000,
          position: "bottom-center",
          unstyled: true,
          classNames: {
            toast:
              "rounded-full px-5 py-2.5 text-sm font-body font-semibold shadow-warm-lg",
          },
          style: { backgroundColor: "#1A1A0F", color: "#FFFFFF" },
        });
      } else {
        toast.error("Couldn't share — try copying the URL from your browser bar.");
      }
    }

    settledRef.current = true;
    track("post_checkin_share_tapped", {
      place_id: park.id,
      share_method: method,
    });
    setMounted(false);
    setTimeout(() => onClose?.(), 220);
  };

  // Visit-note ("Dog Journal") textarea. Sits below the stat strip
  // inside the same bottom sheet. Renders only when:
  //   1. The save flow isn't already complete (after `Saved ✓` we
  //      keep the confirmation visible but lock the textarea so the
  //      user can't trigger a double-save).
  //   2. We have at least one check-in ID to PATCH (the prompt opens
  //      with checkinIds from a fresh POST /checkins response — if
  //      somehow empty, we hide the input rather than fail silently).
  const renderNoteInput = () => {
    if (!checkinIds || checkinIds.length === 0) return null;
    const charCount = note.length;
    const showCounter = charCount > 0;
    const canSave = note.trim().length > 0 && saveState === "idle";
    const saved = saveState === "saved";
    return (
      <div
        data-testid="post-checkin-note"
        className="font-body"
        style={{
          marginTop: 10,
          paddingTop: 10,
          borderTop: "1px solid #E8E4D8",
        }}
      >
        <textarea
          data-testid="post-checkin-note-input"
          value={note}
          onFocus={handleNoteFocus}
          onChange={handleNoteChange}
          disabled={saved || saveState === "saving"}
          placeholder="Add a note about your visit"
          rows={1}
          maxLength={280}
          aria-label="Add a note about your visit"
          className="w-full font-body resize-none"
          style={{
            backgroundColor: "transparent",
            color: "#1A1A0F",
            border: 0,
            outline: "none",
            padding: 0,
            fontSize: 14,
            lineHeight: 1.4,
            // Expand up to 3 lines (≈ 14px × 1.4 × 3 = 58.8px).
            maxHeight: 60,
            minHeight: 20,
            overflowY: "auto",
            opacity: saved ? 0.6 : 1,
          }}
        />
        {(showCounter || canSave || saved) && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: 6,
              gap: 8,
            }}
          >
            <span
              data-testid="post-checkin-note-counter"
              style={{
                fontSize: 11,
                color: charCount >= 280 ? "#B85C38" : "#8a857d",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {showCounter ? `${charCount}/280` : ""}
            </span>
            {saved ? (
              <span
                data-testid="post-checkin-note-saved"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#4A7C59",
                }}
              >
                <CheckIcon className="w-3.5 h-3.5" strokeWidth={3} />
                Saved
              </span>
            ) : (
              canSave && (
                <button
                  type="button"
                  data-testid="post-checkin-note-save"
                  onClick={handleSaveNote}
                  disabled={saveState === "saving"}
                  className="font-body font-semibold rounded-lg active:scale-95 transition-transform"
                  style={{
                    backgroundColor: "#4A7C59",
                    color: "#FFFFFF",
                    fontSize: 12,
                    padding: "6px 12px",
                    opacity: saveState === "saving" ? 0.7 : 1,
                  }}
                >
                  {saveState === "saving" ? "Saving…" : "Save"}
                </button>
              )
            )}
          </div>
        )}
      </div>
    );
  };

  if (!open || !park) return null;

  // Post-check-in stat strip — sits below the share CTA row inside the
  // same bottom sheet. Renders only when the backend included a
  // `user_stats` block on the check-in response (older clients without
  // the field still get the original UI).
  const renderStatStrip = () => {
    if (!userStats || !userStats.total_checkins) return null;
    const secondLine = userStats.is_new_park
      ? `🎉 New park unlocked! ${userStats.unique_parks} park${userStats.unique_parks === 1 ? "" : "s"} explored so far.`
      : `You\u2019ve been here ${userStats.times_at_park} time${userStats.times_at_park === 1 ? "" : "s"}.`;
    return (
      <div
        data-testid="post-checkin-stats"
        className="font-body"
        style={{ marginTop: 8, fontSize: 14, color: "#4A7C59", lineHeight: 1.4 }}
      >
        <div data-testid="post-checkin-visit-num">
          {`🐾 That\u2019s visit #${userStats.total_checkins}!`}
        </div>
        <div
          data-testid="post-checkin-secondline"
          style={{ color: "#6b6560", marginTop: 2 }}
        >
          {secondLine}
        </div>
      </div>
    );
  };

  return (
    <div
      data-testid="post-checkin-share-prompt"
      role="region"
      aria-label="Share your check-in"
      className="fixed inset-x-0 bottom-0 z-50 px-4 pointer-events-none"
      style={{ paddingBottom: "max(16px, env(safe-area-inset-bottom))" }}
    >
      <div
        className="mx-auto max-w-xl pointer-events-auto"
        style={{
          backgroundColor: "#FFFFFF",
          borderRadius: "12px",
          padding: "12px 14px",
          boxShadow: "0 -2px 8px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.06)",
          transform: mounted ? "translateY(0)" : "translateY(140%)",
          transition: "transform 200ms ease-out",
        }}
      >
        <div className="flex items-center gap-3">
          <PawPrint className="w-5 h-5 shrink-0" style={{ color: "#4A7C59" }} />
          <p
            className="flex-1 font-body m-0"
            style={{ fontSize: "14px", color: "#1A1A0F", lineHeight: 1.35 }}
          >
            You're checked in! Let your crew know you're here.
          </p>
          <button
            type="button"
            data-testid="post-checkin-share-btn"
            onClick={handleShare}
            className="shrink-0 font-body font-semibold rounded-lg active:scale-95 transition-transform"
            style={{
              backgroundColor: "#4A7C59",
              color: "#FFFFFF",
              fontSize: "13px",
              padding: "8px 16px",
            }}
          >
            Share
          </button>
          <button
            type="button"
            data-testid="post-checkin-share-dismiss"
            onClick={handleDismiss}
            aria-label="Dismiss share prompt"
            className="shrink-0 flex items-center justify-center active:scale-95 transition-transform"
            style={{
              color: "#7A7A60",
              // 11px padding + 20px icon = 42px tap target (close to WCAG 44).
              padding: 11,
              background: "transparent",
              border: 0,
              minWidth: 42,
              minHeight: 42,
            }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        {renderStatStrip()}
        {renderNoteInput()}
      </div>
    </div>
  );
};

export default PostCheckinSharePrompt;
